import { useState, type FormEvent } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useI18n } from "@/i18n";
import { forgotPassword, resetPassword, forgotNumber } from "@/api/endpoints";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AuthLangSwitcher } from "@/components/auth-lang-switcher";
import { OtpInput } from "@/components/otp-input";
import { Logo } from "@/components/logo";
import { Eye, EyeOff } from "lucide-react";

export function LoginPage() {
  const { isAuthenticated, isStaff, staffVenueId, login, staffLogin } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [venueNumber, setVenueNumber] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState<"owner" | "staff">("owner");

  // Reset password state
  const [resetMode, setResetMode] = useState<"none" | "email" | "code" | "forgot-number">("none");
  const [resetEmail, setResetEmail] = useState("");
  const [forgotNumberEmail, setForgotNumberEmail] = useState("");
  const [forgotNumberSent, setForgotNumberSent] = useState(false);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetMsg, setResetMsg] = useState("");

  if (isAuthenticated) {
    if (isStaff && staffVenueId) {
      return <Navigate to={`/venues/${staffVenueId}/orders`} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (loginMode === "staff") {
        await staffLogin(Number(venueNumber), pin);
        // staffVenueId will be set after state update, Navigate handles redirect
        navigate("/", { replace: true });
      } else {
        await login(Number(venueNumber), password);
        navigate("/", { replace: true });
      }
    } catch {
      setError(t("invalid_credentials"));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(resetEmail);
      setResetMode("code");
      setResetMsg(t("reset_code_sent"));
    } catch {
      setError(t("error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await resetPassword(resetEmail, resetCode, newPassword);
      setResetMode("none");
      setResetMsg("");
      setError("");
      setPassword("");
    } catch {
      setError(t("invalid_expired_code"));
    } finally {
      setLoading(false);
    }
  };

  // Forgot password - enter email
  if (resetMode === "email") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
      <AuthLangSwitcher />
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t("forgot_password_title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("enter_email")}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleForgot} className="space-y-4">
              <Input
                type="email"
                placeholder={t("email")}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? t("sending") : t("send_code")}
              </Button>
            </form>
            <button
              className="mt-4 block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              onClick={() => { setResetMode("none"); setError(""); }}
            >
              {t("back_to_login")}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot password - enter code + new password
  if (resetMode === "code") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
      <AuthLangSwitcher />
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t("reset_password")}</CardTitle>
            <p className="text-sm text-muted-foreground">{resetMsg || t("enter_code_desc")}</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleReset} className="space-y-5">
              <OtpInput
                value={resetCode}
                onChange={setResetCode}
                length={6}
                disabled={loading}
              />
              {/* Password input — autofill is told this is a brand-new password
                  (not a username, fixing the popup-injects-email surprise),
                  and the eye button toggles visibility so the user can confirm
                  what they typed. The input itself is centered to align with
                  the OTP cells above. */}
              <div className="relative">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  placeholder={t("new_password")}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={6}
                  required
                  autoComplete="new-password"
                  className="pr-10 text-center"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword((s) => !s)}
                  aria-label={showNewPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || resetCode.length !== 6}>
                {loading ? t("resetting") : t("change_password")}
              </Button>
            </form>
            {/* Recovery hatch: 15-minute TTL is easy to miss; a single-click
                resend keeps the user from bouncing back to the email screen. */}
            <button
              type="button"
              className="mt-3 block w-full text-center text-sm text-primary hover:underline disabled:opacity-50"
              disabled={loading}
              onClick={async () => {
                setError("");
                setLoading(true);
                try {
                  await forgotPassword(resetEmail);
                  setResetCode("");
                  setResetMsg(t("reset_code_sent"));
                } catch {
                  setError(t("error_occurred"));
                } finally {
                  setLoading(false);
                }
              }}
            >
              {t("resend_code")}
            </button>
            <button
              className="mt-2 block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              onClick={() => { setResetMode("none"); setError(""); setResetCode(""); setNewPassword(""); }}
            >
              {t("back_to_login")}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Forgot venue number
  if (resetMode === "forgot-number") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
      <AuthLangSwitcher />
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t("forgot_number_title")}</CardTitle>
            <p className="text-sm text-muted-foreground">{t("forgot_number_desc")}</p>
          </CardHeader>
          <CardContent>
            {forgotNumberSent ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-green-600">{t("venue_number_sent")}</p>
                <Button className="w-full" onClick={() => { setResetMode("none"); setError(""); }}>
                  {t("back_to_login")}
                </Button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setError("");
                setLoading(true);
                try {
                  await forgotNumber(forgotNumberEmail);
                  setForgotNumberSent(true);
                } catch {
                  setError(t("error_occurred"));
                } finally {
                  setLoading(false);
                }
              }} className="space-y-4">
                <Input
                  type="email"
                  placeholder={t("email")}
                  value={forgotNumberEmail}
                  onChange={(e) => setForgotNumberEmail(e.target.value)}
                  required
                />
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? t("sending") : t("send_venue_number")}
                </Button>
              </form>
            )}
            <button
              className="mt-4 block w-full text-center text-sm text-muted-foreground hover:text-foreground"
              onClick={() => { setResetMode("none"); setError(""); }}
            >
              {t("back_to_login")}
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Normal login
  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <AuthLangSwitcher />
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          {/* Brand: full lockup replaces the H-monogram placeholder + duplicate
              "HereMenu" CardTitle. The wordmark already says the name, so the
              title is redundant. */}
          <Logo size="lg" variant="full" className="mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {loginMode === "staff" ? t("staff_login_subtitle") : t("login_subtitle")}
          </p>
        </CardHeader>
        <CardContent>
          {/* Login mode toggle */}
          <div className="flex rounded-lg border mb-4 overflow-hidden">
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-medium transition-colors ${loginMode === "owner" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => { setLoginMode("owner"); setError(""); }}
            >
              {t("login")}
            </button>
            <button
              type="button"
              className={`flex-1 py-2 text-sm font-medium transition-colors ${loginMode === "staff" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => { setLoginMode("staff"); setError(""); }}
            >
              {t("staff_login")}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="number"
              placeholder={t("venue_number")}
              value={venueNumber}
              onChange={(e) => setVenueNumber(e.target.value)}
              required
            />
            {loginMode === "staff" ? (
              <Input
                type="password"
                inputMode="numeric"
                maxLength={4}
                placeholder={t("staff_pin_placeholder")}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                required
                className="text-center text-2xl tracking-[0.5em]"
              />
            ) : (
              <Input
                type="password"
                placeholder={t("password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? t("logging_in") : t("login")}
            </Button>
          </form>

          {loginMode === "owner" && (
            <>
              <div className="mt-3 flex justify-center gap-4">
                <button
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => setResetMode("email")}
                >
                  {t("forgot_password")}
                </button>
                <span className="text-muted-foreground">·</span>
                <button
                  className="text-sm text-muted-foreground hover:text-foreground"
                  onClick={() => { setResetMode("forgot-number"); setForgotNumberSent(false); setForgotNumberEmail(""); setError(""); }}
                >
                  {t("forgot_number")}
                </button>
              </div>
              <p className="mt-2 text-center text-sm text-muted-foreground">
                {t("no_account")}{" "}
                <Link to="/register" className="font-medium text-primary hover:underline">
                  {t("register")}
                </Link>
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
